import { useCallback, useRef, useState } from 'react';
import { ImagePlus, Lock, Loader2, Maximize2, RefreshCw, Trash2, FolderOpen } from 'lucide-react';
import { useSketch } from './useSketch';
import { useToleranceStore } from '../../../stores/toleranceStore';
import { SKETCH_ACCEPT } from '../../../utils/sketchFile';
import SketchLightbox from './SketchLightbox';

// Skizzenfläche neben/statt der Maßkette (C7). Zustände aus sketchSlot:
// bild, leer, pro, projekt. `compact` = schmale Kachel (Beides ohne Skizze).
export default function SketchPanel({ controls = null, compact = false }) {
  const { sketchUrl, slot, canAdd, canRemove, uploading, addSketch, deleteSketch } = useSketch();
  const name = useToleranceStore((s) => s.calculationName);
  const inputRef = useRef(null);
  const [gross, setGross] = useState(false);
  const [ziehen, setZiehen] = useState(false);
  // Stabile Referenz, sonst setzt useFocusTrap bei jedem Render neu auf.
  const schliessen = useCallback(() => setGross(false), []);

  const pick = () => { if (canAdd && !uploading) inputRef.current?.click(); };
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) addSketch(file);
  };
  const dropProps = canAdd ? {
    onDragOver: (e) => { e.preventDefault(); setZiehen(true); },
    onDragLeave: () => setZiehen(false),
    onDrop: (e) => {
      e.preventDefault();
      setZiehen(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) addSketch(file);
    },
  } : {};

  const alt = `Skizze zu ${name || 'dieser Berechnung'}`;
  const aktion = 'inline-flex items-center justify-center bg-surface/95 border border-line text-ink-2 hover:text-ink';
  const aktionStyle = { width: 28, height: 28, borderRadius: 6, cursor: 'pointer' };
  const hinweis = 'flex flex-col items-center justify-center gap-2 text-ink-3 h-full text-center';

  let body;
  if (uploading) {
    body = (
      <div className="flex items-center justify-center gap-2 text-ink-3 h-full" style={{ fontSize: 12 }}>
        <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Wird hochgeladen …
      </div>
    );
  } else if (slot === 'bild') {
    // Heller Grund auch im Dunkelmodus: Zeichnungen sind meist schwarz auf weiß.
    body = (
      <div className="relative group h-full" style={{ background: '#fff' }}>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setGross(true)}
          className="block w-full h-full"
          style={{ cursor: 'zoom-in', border: 'none', padding: 0, background: 'transparent' }}
        >
          <img src={sketchUrl} alt={alt} className="w-full h-full" style={{ objectFit: 'contain', maxHeight: 320 }} />
        </button>
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button type="button" aria-label="Skizze vergrößern" title="Vergrößern" onClick={() => setGross(true)} className={aktion} style={aktionStyle}>
            <Maximize2 size={14} aria-hidden="true" />
          </button>
          {canAdd && (
            <button type="button" aria-label="Skizze ersetzen" title="Ersetzen" onClick={pick} className={aktion} style={aktionStyle}>
              <RefreshCw size={14} aria-hidden="true" />
            </button>
          )}
          {canRemove && (
            <button type="button" aria-label="Skizze entfernen" title="Entfernen" onClick={deleteSketch} className={`${aktion} hover:text-err`} style={aktionStyle}>
              <Trash2 size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  } else if (slot === 'leer') {
    body = (
      <button
        type="button"
        onClick={pick}
        className={`w-full h-full flex flex-col items-center justify-center gap-2 text-ink-3 hover:text-ink-2 ${ziehen ? 'bg-brand-soft' : ''}`}
        style={{
          border: '1.5px dashed var(--color-line-strong)', borderRadius: 'var(--radius-md)',
          cursor: 'pointer', background: ziehen ? undefined : 'transparent', fontSize: 12, padding: 12,
        }}
        title="Skizze hinzufügen: auswählen, hierher ziehen oder Strg+V"
      >
        <ImagePlus size={compact ? 18 : 22} aria-hidden="true" />
        {compact ? '+ Skizze' : 'Skizze hierher ziehen, einfügen (Strg+V) oder auswählen'}
      </button>
    );
  } else if (slot === 'pro') {
    body = (
      <div className={hinweis} style={{ fontSize: 12, padding: 12 }}>
        <Lock size={16} aria-hidden="true" /> Eigene Skizze ist im Pro-Plan.
      </div>
    );
  } else {
    body = (
      <div className={hinweis} style={{ fontSize: 12, padding: 12 }}>
        <FolderOpen size={16} aria-hidden="true" /> Zum Hinzufügen erst im Projekt speichern.
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, paddingLeft: 4, flexWrap: 'wrap', rowGap: 6 }}>
        <h2 className="text-ink-2" style={{ fontSize: 13, fontWeight: 600, margin: 0, letterSpacing: 0.2, textTransform: 'uppercase' }}>
          Skizze
        </h2>
        <span style={{ flex: 1 }} />
        {controls}
      </div>
      <div
        className="bg-surface border border-line"
        style={{
          borderRadius: 'var(--radius-lg)', overflow: 'hidden', flex: 1, minHeight: compact ? 0 : 150,
          display: 'flex', flexDirection: 'column', padding: slot === 'bild' && !uploading ? 0 : 8,
        }}
        {...dropProps}
      >
        <div style={{ flex: 1, minHeight: 0 }}>{body}</div>
      </div>
      {canAdd && (
        <input ref={inputRef} type="file" accept={SKETCH_ACCEPT} onChange={onFile} hidden />
      )}
      {gross && sketchUrl && <SketchLightbox src={sketchUrl} alt={alt} onClose={schliessen} />}
    </>
  );
}
